import { ApplicationConfig, provideBrowserGlobalErrorListeners, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './services/auth.service';
import { ThemeService } from './services/theme.service';
import { CartService } from './services/cart.service';
import { WishlistService } from './services/wishlist.service';

function initializeAuth(authService: AuthService) {
  // Returning the Promise so APP_INITIALIZER blocks until the AM token is
  // fetched. Without this, the first user-triggered request can race past
  // bootstrap and fire before any token is in place.
  return () => authService.init();
}

function initializeTheme(themeService: ThemeService) {
  return () => themeService.init();
}

function initializeCart(cartService: CartService) {
  return () => cartService.init();
}

function initializeWishlist(wishlistService: WishlistService) {
  return () => wishlistService.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
    provideClientHydration(withEventReplay()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
    MessageService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCart,
      deps: [CartService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWishlist,
      deps: [WishlistService],
      multi: true,
    },
  ],
};
